import { getAssistantSurface } from './assistant-actions'
import type { ExecuteClientToolDeps } from './client-tool-types'

const VIEWS = ['users', 'groups'] as const
type SettingsView = (typeof VIEWS)[number]

/**
 * A page reads its list before it reports it, and a cold users Lambda can take several seconds to
 * answer; past this the page is shown without a claim.
 */
const SHOW_WAIT_MS = 8_000
const POLL_MS = 100

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

const pathOf = (view: SettingsView): string => (view === 'groups' ? '/settings/groups' : '/settings/users')

/** True once the Settings page on screen reports the requested view, loaded. */
function showing(view: SettingsView): boolean {
  const handle = getAssistantSurface()
  if (handle?.kind !== 'settings') return false

  const ctx = handle.readContext().settings

  // A page that is still (re)reading reports the previous page's rows.
  return ctx.view === view && !ctx.loading
}

async function awaitShowing(view: SettingsView): Promise<boolean> {
  const deadline = Date.now() + SHOW_WAIT_MS

  for (;;) {
    if (showing(view)) return true
    if (Date.now() >= deadline) return false
    await delay(POLL_MS)
  }
}

/**
 * `show_settings`. `refusal` is the route manifest's answer for the target page for this caller (both
 * pages are admin only), resolved by `executeClientTool`, or null when allowed.
 */
export async function executeShowSettings(
  input: unknown,
  deps: ExecuteClientToolDeps,
  refusalFor: (route: string) => string | null,
): Promise<unknown> {
  const rawView = (input as { view?: unknown } | null)?.view

  if (!VIEWS.includes(rawView as SettingsView)) return { error: "Invalid view: expected 'users' or 'groups'." }

  const view = rawView as SettingsView
  const target = pathOf(view)
  const refusal = refusalFor(target)
  if (refusal) return { refused: refusal }

  const onTargetPage = deps.currentRoute() === target
  const discard = (input as { discardUnsaved?: unknown }).discardUnsaved === true

  if (!onTargetPage && !discard) {
    const unsaved = deps.hasUnsavedWork()
    if (unsaved) return { blocked_unsaved_work: unsaved }
  }

  // Showing what is already shown must not add a Back entry.
  if (onTargetPage) deps.navigate(target, { replace: true })
  else deps.navigate(target)

  return (await awaitShowing(view))
    ? { shown: true, view }
    : { shown: true, note: `The ${view} page did not report its list in time; it is open but may still be loading.` }
}
